import { Spin } from 'antd';
import { Suspense, lazy } from 'react';
import { useSearchParams } from 'react-router-dom';
import SharePointMsalProvider from '../../components/SharePoint/SharePointMsalProvider';
import {
  getFilegrantViewerBaseUrl,
  isFilegrantViewerEnabled,
} from '../../lib/filegrantViewerChoice';
import { SHAREPOINT_VIEWER_PARAM } from '../../lib/sharePoint';
import { isViewerEmbedSession } from '../../utils/viewerWebviewAuth';
import FilegrantViewerHost from './FilegrantViewerHost';
import Viewer from './Viewer';

const SharePointViewer = lazy(() => import('../SharePointViewer'));

/**
 * Entry point of `/viewer/:fileId`.
 *
 * React Router cannot match on the query string, so the choice happens here: with
 * `?sharepoint=true` the id is a SharePoint sharing token and the file is read (and
 * decrypted) from SharePoint; otherwise it is a FileGrant file id and the usual viewer runs.
 *
 * When `VITE_APP_FILEGRANT_VIEWER_URL` is set (and not a native webview embed), FileGrant
 * files open in an iframe host shell (ViewerGrant `/open`). Empty URL → classic Viewer.
 * SharePoint and native webview embeds stay on the classic path.
 *
 * The parameter only selects the source: it authorizes nothing. Both branches authorize on
 * the user's own token.
 *
 * The SharePoint branch is wrapped in its own MSAL provider: the delegated Graph token comes from the
 * CUSTOMER's tenant and its app registration, which are per company and read from the API — see
 * `SharePointMsalProvider`. The FileGrant branch needs none of that.
 */
const ViewerDispatcher = () => {
  const [searchParams] = useSearchParams();
  const isSharePointFile = searchParams.get(SHAREPOINT_VIEWER_PARAM) === 'true';

  if (isSharePointFile) {
    return (
      <Suspense
        fallback={
          <div className="flex h-full w-full items-center justify-center">
            <Spin size="large" />
          </div>
        }
      >
        <SharePointMsalProvider>
          <SharePointViewer />
        </SharePointMsalProvider>
      </Suspense>
    );
  }

  if (isFilegrantViewerEnabled() && !isViewerEmbedSession()) {
    return <FilegrantViewerHost viewerBaseUrl={getFilegrantViewerBaseUrl()} />;
  }

  return <Viewer />;
};

export default ViewerDispatcher;
